import { isAxiosError } from "axios";
import { Controller, useForm } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { Select } from "src/base-components/Select";
import { DEFAULT_STRING_LENGTH } from "src/base-components/schemaKit/validators";
import {
  useCaseTemplateWithVersions,
  useDuplicateCaseTemplateVersion,
} from "src/cases/queries/templates";
import { TemplateVersionName } from "src/cases/templates/TemplateVersionName";
import { JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel } from "src/clients/case-management";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { CaseTemplateVersionsPageParams } from "src/router/urls";
import { logger } from "src/utils/logger";
import { getApiErrorDetail } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

type DuplicateTemplateModalProps = {
  open: boolean;
  onClose: () => void;
  versionId?: string;
  onDuplicateSuccess?: (
    data: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
  ) => void;
};

type DuplicateTemplateForm = {
  templateId: string;
  versionId: string;
  versionName: string;
};

export const DuplicateTemplateVersionModal: React.FC<
  DuplicateTemplateModalProps
> = ({ open, onClose: onCloseProp, onDuplicateSuccess, versionId }) => {
  const { templateId } = useParamsDecode<CaseTemplateVersionsPageParams>();
  const { data: template, isLoading } = useCaseTemplateWithVersions(templateId);
  const { mutateAsync: duplicateVersion } = useDuplicateCaseTemplateVersion();
  const form = useForm<DuplicateTemplateForm>({
    mode: "onChange",
    values: {
      templateId: templateId,
      versionId: versionId ?? template?.versions?.[0]?.id ?? "",
      versionName: "",
    },
  });

  const onClose = () => {
    form.reset();
    onCloseProp();
  };

  const onSubmit = async (data: DuplicateTemplateForm) => {
    try {
      const newVersion = await duplicateVersion(data);
      onClose();
      onDuplicateSuccess?.(newVersion);
    } catch (error) {
      logger.error("Failed to duplicate version", error);

      if (isAxiosError(error)) {
        toastActions.failure({
          title: "Failed to duplicate version",
          description: getApiErrorDetail(error),
        });

        return;
      }

      toastActions.failure({
        title: "Failed to duplicate version",
        description: "An unknown error occurred",
      });
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>Create version</Modal.Header>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Modal.Content>
          <div className="flex flex-col gap-5">
            <div className="flex flex-col">
              <Label mb="mb-0.5" required>
                Create from
              </Label>
              <span className="mb-2 font-inter-normal-12px text-gray-500">
                Select a version to duplicate
              </span>
              <Controller
                control={form.control}
                name="versionId"
                render={({ field, fieldState }) => (
                  <>
                    <Select
                      dataLoc="duplicate-template-version-select"
                      errored={!!fieldState.error}
                      options={
                        template?.versions?.map((version) => ({
                          key: version.id,
                          value: (
                            <TemplateVersionName
                              name={version.attributes.version_name}
                              state={version.attributes.publish_state}
                            />
                          ),
                        })) ?? []
                      }
                      value={field.value}
                      onChange={field.onChange}
                    />
                    {fieldState.error?.message && (
                      <ErrorHint>{fieldState.error.message}</ErrorHint>
                    )}
                  </>
                )}
                rules={{
                  required: "Please select a version to create from",
                }}
              />
            </div>
            <div>
              <Label required>Version name</Label>
              <Input
                errored={!!form.formState.errors.versionName}
                fullWidth
                {...form.register("versionName", {
                  required: "Version name is required",
                  maxLength: {
                    value: DEFAULT_STRING_LENGTH,
                    message: `Version name must be less than ${DEFAULT_STRING_LENGTH} characters`,
                  },
                })}
                placeholder="Type version name"
              />
              {form.formState.errors.versionName && (
                <ErrorHint>
                  {form.formState.errors.versionName?.message}
                </ErrorHint>
              )}
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              disabled={
                isLoading ||
                form.formState.isSubmitting ||
                !form.formState.isValid
              }
              htmlType="submit"
              loading={isLoading || form.formState.isSubmitting}
              variant="primary"
            >
              Create
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
